// ============================================================================
// Autore: Salvo de Luca
// Azienda Dialogicom s.r.l.s.
// Nome file: VerifyEmailPage.tsx
// Percorso: frontend3/src/components/auth/VerifyEmailPage.tsx
// Release: v1.0.0 - Verifica email automatica
// Data: 27/06/2025
// ============================================================================

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const VerifyEmailPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'pending'>('loading');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const pending = params.get('pending');
    const email = params.get('email');

    if (pending === '1') {
      setStatus('pending');
      setMessage(
        email
          ? `Ti abbiamo inviato un'email di verifica a ${email}. Clicca sul link per completare la registrazione.`
          : `Ti abbiamo inviato un'email di verifica. Clicca sul link per completare la registrazione.`
      );
      return;
    }

    if (!code) {
      setStatus('error');
      setMessage('Codice di verifica mancante.');
      return;
    }

    // Chiama l'API di verifica
    const apiUrl = email 
      ? `/api/auth/verify-email?code=${encodeURIComponent(code)}&email=${encodeURIComponent(email)}`
      : `/api/auth/verify-email?code=${encodeURIComponent(code)}`;
    
    fetch(apiUrl, {
      method: 'GET'
    })
      .then(async (res) => {
        const data = await res.json();
        if (res.ok && data.success) {
          setStatus('success');
          setMessage('Email verificata con successo! Ora puoi accedere.');
          setTimeout(() => navigate('/sign-in'), 3000);
        } else {
          setStatus('error');
          setMessage(data.error || 'Errore durante la verifica.');
        }
      })
      .catch(() => {
        setStatus('error');
        setMessage('Errore di connessione al server.');
      });
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white">
      <div className="bg-slate-800 p-8 rounded-xl shadow-xl text-center max-w-md w-full">
        <h1 className="text-2xl font-bold mb-4">
          {status === 'loading' && 'Verifica in corso...'}
          {status === 'success' && 'Verifica completata'}
          {status === 'error' && 'Errore di verifica'}
          {status === 'pending' && 'Controlla la tua email'}
        </h1>
        <p className={status === 'success' ? 'text-green-400' : status === 'error' ? 'text-red-400' : 'text-gray-300'}>
          {message}
        </p>
        {status === 'success' && (
          <p className="mt-4 text-sm text-gray-400">Verrai reindirizzato al login...</p>
        )}
        {status === 'pending' && (
          <button
            className="mt-6 px-4 py-2 bg-green-600 rounded hover:bg-green-500 transition"
            onClick={() => navigate('/sign-in')}
          >
            Vai al login
          </button>
        )}
        {status === 'error' && (
          <button
            className="mt-6 px-4 py-2 bg-green-600 rounded hover:bg-green-500 transition"
            onClick={() => navigate('/sign-in')}
          >
            Torna al login
          </button>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage; 